import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import type { Chart as ChartJS } from 'chart.js';
import {
  buildRev2ChartBundle,
  rev2ChartLabelForView,
  type Rev2ChartViewKey,
} from '../../domains/capAnchoredActivity/canonical/rev2ChartViews';
import type { CanonicalEnergyActivityYear } from '../../domains/capAnchoredActivity/canonical/types';
import { GHG_INVENTORY_CHART_HEIGHT_PX } from '../../theme/ghgInventoryChartLayout';
import { BURBANK_CAP_ANCHORED_DESCRIPTION } from '../../domains/capAnchoredActivity/burbankCapAnchoredCopy';

export interface BurbankBuildingsReconciledChartProps {
  location: string;
  view: Rev2ChartViewKey;
  electricityRows: CanonicalEnergyActivityYear[];
  chartRef?: React.Ref<ChartJS<'bar' | 'line'> | null>;
}

export const BurbankBuildingsReconciledChart: React.FC<BurbankBuildingsReconciledChartProps> = ({
  location,
  view,
  electricityRows,
  chartRef,
}) => {
  const chartBundle = useMemo(() => {
    if (!electricityRows.length) return null;
    return buildRev2ChartBundle(electricityRows, view, 'activity', { jurisdictionName: location });
  }, [electricityRows, view, location]);

  const title = rev2ChartLabelForView(view, true, undefined);

  if (!chartBundle) {
    return (
      <div style={{ minHeight: 200, color: '#6b7280' }} role="status">
        No reconciled chart data for this view.
      </div>
    );
  }

  return (
    <div className="burbank-buildings-reconciled-chart">
      <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px 0' }} role="note">
        <strong>Reconciled pilot (EIA-861 + published CAP):</strong> {BURBANK_CAP_ANCHORED_DESCRIPTION}
      </p>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px 0' }}>
        {title} — {location}
      </p>
      <div style={{ height: GHG_INVENTORY_CHART_HEIGHT_PX }}>
        <Chart
          ref={chartRef as never}
          type="bar"
          data={chartBundle.data}
          options={chartBundle.options}
        />
      </div>
    </div>
  );
};
